import { AnyAction, combineReducers, configureStore } from '@reduxjs/toolkit';
import { createBrowserHistory } from 'history';
import authReducer from 'modules/auth/store';
import { authSignOut } from 'modules/auth/store/actions';
import { saveState } from 'modules/auth/store/persist';
import masterBoardReducer from 'modules/board-master/store';
import clientTournament from 'modules/client/tournaments/store/client-tournament/client-tournament-store';
import devicesReducer from 'modules/devices/store';
import gamesReducer from 'modules/games/store';
import locationsReducer from 'modules/locations/store';
import mainPageReducer from 'modules/main/store/main-page/main-page-store';
import managerReducer from 'modules/manager/store';
import notificationsReducer from 'modules/notifications/store/notifications';
import organizerReducer from 'modules/organizer/store/organizer';
import socialNetsReducer from 'modules/organizer/store/social-nets/social-nets-store';
import organizersReducer from 'modules/organizers/store';
import overlayV2Reducer from 'modules/overlay-v2/store';

import TournamentUsersReducer from 'modules/players/store';
import rolesManagerReducer from 'modules/roles-manager/store/roles-manager/roles-manager-store';
import scheduleReducer from 'modules/schedule/store';
import teamsReducer from 'modules/teams/store';
import entryRequestsReducer from 'modules/teams/store/entry-requests';
import playersReducer from 'modules/teams/store/players';
import templatesEditorReducer from 'modules/template-editor/store';
import championshipsReducer from 'modules/championship/store';
import { createReduxHistoryContext } from 'redux-first-history';
import { baseApi } from 'shared/api/base-api';
import { restApi } from 'shared/api/rest-api';

const { createReduxHistory, routerMiddleware, routerReducer } = createReduxHistoryContext({
  history: createBrowserHistory(),
});

const appReducer = combineReducers({
  router: routerReducer,
  auth: authReducer,
  organizer: organizerReducer,
  championships: championshipsReducer,
  games: gamesReducer,
  teams: teamsReducer,
  players: playersReducer,
  overlayV2: overlayV2Reducer,
  templateEditor: templatesEditorReducer,
  organizers: organizersReducer,
  schedule: scheduleReducer,
  tournamentUsersSlice: TournamentUsersReducer,
  masterBoard: masterBoardReducer,
  locations: locationsReducer,
  entryRequests: entryRequestsReducer,
  notifications: notificationsReducer,
  socialNets: socialNetsReducer,
  clientTournament: clientTournament,
  mainPage: mainPageReducer,
  rolesManager: rolesManagerReducer,
  devices: devicesReducer,
  manager: managerReducer,
  [baseApi.reducerPath]: baseApi.reducer,
  [restApi.reducerPath]: restApi.reducer,
});

// При выходе чистим кэши RTK Query, чтобы данные предыдущего пользователя
// (в т.ч. список доступных организаторов) не показывались следующему
export const rootReducer: typeof appReducer = (state, action: AnyAction) => {
  if (action.type === authSignOut.type) {
    const nextState = appReducer(state, action);
    return appReducer(appReducer(nextState, baseApi.util.resetApiState()), restApi.util.resetApiState());
  }

  return appReducer(state, action);
};

const defaultMiddlewareConfig = {
  serializableCheck: {
    ignoredPaths: [
      'templateEditor.templates.templates',
      'templateEditor.editor.fabricManager',
      'templateEditor.templates.catalog',
      'schedule.selectedDate',
      'schedule.weeks',
      'manager.activeBoard',
      // Окна оверлея, открытые панелью управления: это объекты Window, а не данные.
      'overlayV2.overlay.popups',
    ],
  },
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware(defaultMiddlewareConfig).concat(routerMiddleware, baseApi.middleware, restApi.middleware),
  devTools: process.env.NODE_ENV !== 'production',
});

store.subscribe(() => {
  saveState(store.getState());
});

export const history = createReduxHistory(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
